/**
 * joursCivils — compter des JOURS, jamais des blocs de 24 h (décisions PO D186 et D187).
 *
 * Deux questions, une fonction chacune :
 *
 *  • « Combien de jours AVANT cette date ? » (D186) — une date NUE (colonne `DATE` : validité
 *    d'un devis, date de conclusion prévue) moins le jour de l'ORGANISME (`useJourDeLOrganisme`,
 *    servi par `GET /api/tenant/config` → `today`). Lue par les DEVIS : la fiche (« Validité —
 *    J‑N »), la liste (« Expire dans N j ») et la tuile du kanban (badge ⏳, « Closing
 *    dépassé ») — trois écrans, un seul compte.
 *
 *  • « Combien de jours DEPUIS cet instant ? » (D187) — le jour de l'instant et le jour de
 *    maintenant, lus dans le fuseau de l'organisme quand l'écran le connaît, sinon dans celui
 *    du poste. « Hier » est la VEILLE, pas « entre 24 et 48 h » : un événement de 23 h lu le
 *    surlendemain à 1 h 30 s'écrit « Il y a 2 jours ». Lue par les deux outils partagés de
 *    `date-utils` (`formatRelativeTime`, `formatTimelineAge`).
 *
 * ⚠️ D'AUTRES COPIES EXISTENT ENCORE, hors de ces écrans (liste « après la série », famille
 * D186/D187) : « Hier » en blocs de 24 h au jour du poste dans
 * `pages/catalog/articles/components/ArticleStockHelpers.tsx`, `pages/knowledge/PlanBadge.tsx`
 * et `pages/developers/DeveloperPortalSections.tsx` ; « jours avant / depuis » dans
 * `components/crm/framework-contracts/frameworkContractsHelpers.ts` (`daysUntil`, jour de
 * l'organisme), `pages/quality/components/qualityKanbanTile.helpers.ts` (`daysUntil`, jour du
 * poste), `hooks/useQuoteBoards.types.ts` (`daysSince` : ancienneté, « Envoyé il y a N j »,
 * jour du poste) et `pages/sales/tenders/components/TenderDeadlineChip.helpers.ts`
 * (`daysUntilDeadline`, jour du poste). Une copie de plus se branche ici, pas à côté.
 */
import { toISODate } from './date-utils.iso'

const JOUR_NU = /^([0-9]{4})-([0-9]{2})-([0-9]{2})/

/** Rang d'un jour civil `YYYY-MM-DD` (les dix premiers caractères) : deux rangs se
 *  soustraient en jours, sans heure ni fuseau. `null` pour un jour qui n'existe pas. */
export function rangDuJour(jour: string): number | null {
    const lu = JOUR_NU.exec(jour)
    if (!lu) return null
    const [annee, mois, quantieme] = [Number(lu[1]), Number(lu[2]) - 1, Number(lu[3])]
    const minuit = Date.UTC(annee, mois, quantieme)
    const verif = new Date(minuit)
    if (verif.getUTCMonth() !== mois || verif.getUTCDate() !== quantieme) return null
    return Math.round(minuit / 86_400_000)
}

/** D186 — jours avant une date NUE, comptés depuis `aujourdhui` (le jour de l'organisme,
 *  `YYYY-MM-DD`) : 0 le jour même, 1 la veille, négatif une fois la date passée ; `null`
 *  sans date lisible. */
export function joursAvantUneDateNue(date: string | null | undefined, aujourdhui: string): number | null {
    if (!date) return null
    const cible = rangDuJour(date)
    const depart = rangDuJour(aujourdhui)
    return cible === null || depart === null ? null : cible - depart
}

/** Formateurs `YYYY-MM-DD` par fuseau : en construire un par appel coûte ~34 fois le formatage. */
const formateurs = new Map<string, Intl.DateTimeFormat>()

/** Le jour civil `YYYY-MM-DD` d'un instant, lu dans `fuseau` (IANA), sinon dans celui du poste. */
export function jourCivilDe(instant: Date, fuseau?: string): string {
    if (isNaN(instant.getTime())) return ''
    if (!fuseau) return toISODate(instant)
    let formateur = formateurs.get(fuseau)
    if (!formateur) {
        formateur = new Intl.DateTimeFormat('en-US', {
            timeZone: fuseau,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
        formateurs.set(fuseau, formateur)
    }
    const parties = new Map(formateur.formatToParts(instant).map((p) => [p.type, p.value]))
    return `${parties.get('year')}-${parties.get('month')}-${parties.get('day')}`
}

/** D187 — jours CIVILS entre le jour de `instant` et celui de `maintenant`, lus dans `fuseau`
 *  (celui de l'organisme quand l'écran le connaît) ou dans celui du poste : 0 le même jour,
 *  1 la veille (« Hier »), négatif pour un instant à venir. 0 si l'un des deux est illisible. */
export function joursCivilsEcoules(instant: Date, maintenant: Date, fuseau?: string): number {
    const depuis = rangDuJour(jourCivilDe(instant, fuseau))
    const jusqua = rangDuJour(jourCivilDe(maintenant, fuseau))
    return depuis === null || jusqua === null ? 0 : jusqua - depuis
}
